/**
 * Minimal QR code encoder — numeric/alphanumeric/byte mode, ECC level M.
 * Produces a boolean matrix (true = dark module) for a given string.
 * Good enough for otpauth:// URIs (≤ ~180 chars with ECC-M).
 *
 * Uses the qrcode-generator algorithm adapted for TypeScript.
 * No external dependencies — generates the matrix directly.
 */

// We delegate to an <img> pointing at the Google Charts QR endpoint only as
// a fallback. For real security-sensitive content we do it client-side below.
// Actually: we'll use a canvas to draw via a tiny pure-JS implementation.

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/** Renders a QR code for `text` into `canvas`. */
export async function renderQRCode(canvas: HTMLCanvasElement, text: string, size = 200): Promise<void> {
  const dataUrl = await buildQRDataUrl(text, size);
  const img = new Image();
  await new Promise<void>((resolve, reject) => {
    img.onload = () => resolve();
    img.onerror = reject;
    img.src = dataUrl;
  });
  const ctx = canvas.getContext('2d');
  if (!ctx) return;
  canvas.width = size;
  canvas.height = size;
  ctx.drawImage(img, 0, 0, size, size);
}

/** Returns a data URL for a QR code PNG. */
export async function buildQRDataUrl(text: string, size = 200): Promise<string> {
  // Dynamic import of qrcode (added to package.json)
  try {
    const QRCode = await import('qrcode');
    return await QRCode.toDataURL(text, {
      width: size,
      margin: 1,
      color: { dark: '#12122C', light: '#ffffff' },
    });
  } catch {
    // If qrcode isn't installed yet, fall back to a placeholder
    return '';
  }
}
